import { Component, ErrorInfo, ReactNode } from 'react';
import { CircleAlert as ErrorIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';

interface ErrorBoundaryProps {
  children: ReactNode;
  onReset?: () => void;
  loadFailedLabel: string;
  retryLabel: string;
  unknownErrorLabel: string;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

/**
 * Error boundary to catch Three.js / GLB loading errors
 */
export class ViewerErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('CADModelViewer error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="flex h-full flex-col items-center justify-center gap-4 rounded-md cad-viewer-surface p-6">
          <ErrorIcon className="size-12 text-destructive" />
          <p className="text-center text-sm text-destructive">
            {this.props.loadFailedLabel}
          </p>
          <p className="text-center text-xs text-white/50">
            {this.state.error?.message || this.props.unknownErrorLabel}
          </p>
          <Button
            size="sm"
            variant="outline"
            className="border-white/25 bg-transparent text-white/70 hover:bg-white/10 hover:text-white"
            onClick={() => {
              this.setState({ hasError: false, error: null });
              this.props.onReset?.();
            }}
          >
            {this.props.retryLabel}
          </Button>
        </div>
      );
    }
    return this.props.children;
  }
}

export default ViewerErrorBoundary;
